import { $generateNodesFromDOM } from '@lexical/html';
import { MultilineElementTransformer } from '@lexical/markdown';
import { $isElementNode, $isTextNode, LexicalEditor, LexicalNode } from 'lexical';
import { $createHtmlNode, $isHtmlNode, HtmlNode } from '../../nodes/HtmlNode';
import { $isSuggestionNode } from '../../nodes/SuggestionNode';

export const createHtmlTransformer = (editor: LexicalEditor): MultilineElementTransformer => ({
  type: 'multiline-element',
  dependencies: [HtmlNode],

  regExpStart: /^<html>$/,
  regExpEnd: /^<\/html>$/,

  export: (node: LexicalNode) => {
    if (!$isHtmlNode(node)) {
      return null;
    }

    const exportOutput = node.exportDOM(editor);
    if (!exportOutput?.element) {
      return null;
    }

    const htmlContent = (exportOutput.element as HTMLElement).innerHTML;

    const output: string[] = [];
    output.push('<html>');
    output.push(htmlContent);
    output.push('</html>');
    return output.join('\n');
  },

  replace: (parentNode, _children, startMatch, endMatch, linesInBetween) => {
    if (!startMatch || !endMatch || !linesInBetween) {
      return;
    }

    const htmlString = linesInBetween.join('\n');
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const nodes = $generateNodesFromDOM(editor, doc);

    if (nodes.length === 0) {
      return;
    }

    const htmlNode = $createHtmlNode();
    htmlNode.append(...nodes);
    restoreWhitespaceAroundSuggestions(htmlNode);
    parentNode.append(htmlNode);

    htmlNode.selectEnd();
  },
});

function restoreWhitespaceAroundSuggestions(root: LexicalNode): void {
  if (!$isElementNode(root)) return;

  for (const child of root.getChildren()) {
    if ($isElementNode(child)) {
      restoreWhitespaceAroundSuggestions(child);
    }

    if (!$isSuggestionNode(child)) continue;

    const prev = child.getPreviousSibling();
    const firstChild = child.getFirstChild();
    if (
      $isTextNode(prev) &&
      $isTextNode(firstChild) &&
      /\w$/.test(prev.getTextContent()) &&
      /^\w/.test(firstChild.getTextContent())
    ) {
      prev.setTextContent(prev.getTextContent() + ' ');
    }

    const next = child.getNextSibling();
    const lastChild = child.getLastChild();
    if (
      $isTextNode(next) &&
      $isTextNode(lastChild) &&
      /\w$/.test(lastChild.getTextContent()) &&
      /^\w/.test(next.getTextContent())
    ) {
      next.setTextContent(' ' + next.getTextContent());
    }
  }
}
